import dashboardApi from 'apis/disApi/dashboardApi'
import tileApi from 'apis/disApi/tileApi'
import { isEmpty, unionBy } from 'lodash'
import moment from 'moment'
import { logErrorMessage, buildDatagridDateFilterModel } from 'utils/functions/helpers'
import { datesValues, nonDatesValues } from 'utils/functions/conditionsOptions'
import { correctTimeFormat } from 'utils/functions/doformsDateUtil'

export function sortByPosition(list) {
	if (!Array.isArray(list) || list?.length === 0 || !list) return []

	let sortedList = [...list].sort((a, b) => +a.position - +b.position)

	return sortedList
}

export function transformStringToPositionArr(position) {
	// Loại bỏ các ký tự không phải số từ chuỗi
	var cleanStr = position.replace(/[^\d,-]/g, '')

	// Chuyển đổi chuỗi thành mảng
	var arr = cleanStr.split(',')

	// Chuyển đổi các phần tử trong mảng thành số nguyên
	var result = arr.map(function (item) {
		return parseInt(item.trim())
	})

	return result
}

// Function tìm chỗ trống trong layout để đặt box mới
export function getEmptyPosition(layoutList) {
	let maxX = 0
	let maxY = 0
	for (const item of layoutList) {
		const itemRightX = item.x + item.w
		const itemBottomY = item.y + item.h
		if (itemRightX > maxX) maxX = itemRightX
		if (itemBottomY > maxY) maxY = itemBottomY
	}

	return { x: maxX, y: maxY }
}

export function getTileListWithPosition(tileList) {
	if (tileList?.length === 0) return []
	return tileList.map((tile) => {
		const [x, y, w, h] = transformStringToPositionArr(tile.position)
		return {
			...tile,
			i: tile.name,
			x,
			y,
			w,
			h,
		}
	})
}

export function generateRandomID() {
	return Math.random().toString(36).substring(2)
}

const PRESET_CONDITION_KEYS = ['Master_DateTime', 'Master_Text']
const DATETIME_COLUMNS_KEY = ['Master_DateTime_Start', 'Master_DateTime_End']

export function getAutoUpdateFilters(
	linkedFields,
	selectedFields,
	dashboardKeyList,
	keyDependencies = {}
) {
	try {
		if (linkedFields?.length === 0 || selectedFields?.length === 0)
			return { filterConfigs: [], hasPrimaryKey: false, conditions: {} }

		const result = []
		let conditions = {}
		let hasPrimaryKey = false

		for (let fieldId in linkedFields) {
			const selectedDashboardKey = dashboardKeyList.find((item) => item.id === fieldId)
			const isHasPrimaryKey = Boolean(selectedDashboardKey)
			const keyToGetData =
				selectedDashboardKey?.driverDriveKey ||
				selectedDashboardKey?.mobileUnitKey ||
				selectedDashboardKey?.key
			const isPresetKey = PRESET_CONDITION_KEYS.includes(keyToGetData)

			const filteredValue = selectedFields?.[selectedDashboardKey?.tileKey]?.[keyToGetData]

			const columnName = linkedFields[fieldId]
			if (filteredValue && isHasPrimaryKey) {
				hasPrimaryKey = true
				const fieldId = generateRandomID()
				if (isPresetKey) {
					conditions[columnName] = {
						...filteredValue,
						target: columnName,
						keyData: keyToGetData,
					}
				} else {
					if (DATETIME_COLUMNS_KEY.includes(keyToGetData)) {
						result.push({
							field: columnName,
							operator: keyToGetData === 'Master_DateTime_Start' ? 'onOrAfter' : 'onOrBefore',
							value: moment.utc(filteredValue),
							id: fieldId,
							keyData: keyToGetData,
						})
					} else {
						result.push({
							field: columnName,
							operator: 'contains',
							value: filteredValue,
							id: fieldId,
						})
					}
				}

				if (isEmpty(keyDependencies)) continue

				const dependentKey = keyDependencies[keyToGetData]
				if (!dependentKey) continue

				let dependentValue = selectedFields?.[selectedDashboardKey?.tileKey]?.[dependentKey]
				if (dependentValue) {
					const id = `${fieldId}_${dependentKey}`
					result.push({
						field: dependentKey,
						operator: 'contains',
						value: dependentValue,
						id,
					})
				}
			}
		}
		return { filterConfigs: result, hasPrimaryKey, conditions: conditions }
	} catch (err) {
		return { filterConfigs: [], hasPrimaryKey: false, conditions: {} }
	}
}

export function getPrimaryFilterKeyCondition(selectedFields, dashboardKeyList, primaryFitlers) {
	try {
		if (isEmpty(selectedFields) || isEmpty(primaryFitlers)) return { secondCondition: {} }

		let result = {}
		const selectedDashboardKey = dashboardKeyList.find(
			(dashboardKey) => dashboardKey.id === primaryFitlers.secondConditionLinkedDashboardKey
		)
		const keyToGetData =
			selectedDashboardKey?.driverDriveKey ||
			selectedDashboardKey?.mobileUnitKey ||
			selectedDashboardKey?.key
		const filteredValue = selectedFields?.[selectedDashboardKey?.tileKey]?.[keyToGetData]
		const isPresetKey = PRESET_CONDITION_KEYS.includes(keyToGetData)

		if (filteredValue) {
			if (isPresetKey) {
				result = filteredValue
			} else {
				result = {
					type: 'EQ',
					values: [filteredValue],
				}
			}
		}

		return { secondCondition: result }
	} catch (err) {
		return { secondCondition: {} }
	}
}

export function convertDateTimeValue(value, environment) {
	const hasUserDateTimeFormat = !!environment.userCurrent.time
	const userDateFormat = hasUserDateTimeFormat
		? environment.userCurrent.time.dateFormat.toUpperCase()
		: 'DD/MM/YYYY'
	const userTimeFormat = hasUserDateTimeFormat
		? environment.userCurrent.time.timeFormat
		: 'HH:mm:ss'

	const correctedTimeFormat = correctTimeFormat(userTimeFormat)
	const userOffset = environment?.userCurrent?.time?.timezone?.offset
	if (moment(value, moment.ISO_8601, true).isValid()) {
		const momentValue = moment.utc(value)
		const isMidnight = momentValue.format('HH:mm:ss.SSS') === '00:00:00.000'
		return isMidnight
			? momentValue.format(userDateFormat)
			: momentValue?.utcOffset(userOffset).format(`${userDateFormat} ${correctedTimeFormat}`)
	}
	return value
}

export function getTileLabelValue(labelKey, selectedFields, dashboardKeyList, environment, t) {
	const selectedDashboardKey = dashboardKeyList.find((dashboardKey) => dashboardKey.id === labelKey)

	const isHasPrimaryKey = Boolean(selectedDashboardKey)
	if (!isHasPrimaryKey) return ''
	const keyToGetData =
		selectedDashboardKey?.driverDriveKey ||
		selectedDashboardKey?.mobileUnitKey ||
		selectedDashboardKey?.key
	const isPresetKey = PRESET_CONDITION_KEYS.includes(keyToGetData)
	const filteredValue = selectedFields?.[selectedDashboardKey?.tileKey]?.[keyToGetData]

	if (!filteredValue) return ''
	if (isPresetKey) {
		// master datetime tile
		const preset = filteredValue.preset
		if (preset) {
			const allPresetValues = unionBy(nonDatesValues(t), datesValues(t), 'preset')
			return allPresetValues.find((presetItem) => presetItem.preset === preset)?.name || ''
		}

		const values = filteredValue.values
		const hasUserDateTimeFormat = !!environment.userCurrent.time
		const userDateFormat = hasUserDateTimeFormat
			? environment.userCurrent.time.dateFormat.toUpperCase()
			: 'DD/MM/YYYY'
		const userTimeFormat = hasUserDateTimeFormat
			? environment.userCurrent.time.timeFormat
			: 'HH:mm:ss'

		const correctedTimeFormat = correctTimeFormat(userTimeFormat)
		const userOffset = environment?.userCurrent?.time?.timezone?.offset
		return values.reduce((prevString, currentValue, index) => {
			let value = currentValue
			if (!value) return prevString
			if (moment(currentValue, moment.ISO_8601, true).isValid()) {
				const utcMoment = moment.utc(currentValue)
				if (index === 1) {
					utcMoment.subtract(1, 's')
				}
				value = utcMoment?.utcOffset(userOffset).format(`${userDateFormat} ${correctedTimeFormat}`)
			}

			return prevString.concat(index === 1 ? ' - ' + value : value)
		}, '')
	}

	return convertDateTimeValue(filteredValue, environment)
}

export function getLinkedTitle(
	selectedFields,
	dashboardKeyList,
	firstLabel,
	secondLabel,
	environment,
	t
) {
	try {
		if (
			isEmpty(selectedFields) ||
			isEmpty(dashboardKeyList) ||
			(isEmpty(firstLabel) && isEmpty(secondLabel))
		)
			return { firstLabelValue: '', secondLabelValue: '' }

		const firstLabelValue =
			getTileLabelValue(firstLabel, selectedFields, dashboardKeyList, environment, t) || ''
		const secondLabelValue =
			getTileLabelValue(secondLabel, selectedFields, dashboardKeyList, environment, t) || ''

		return { firstLabelValue, secondLabelValue }
	} catch (err) {
		return { firstLabelValue: '', secondLabelValue: '' }
	}
}

export const processCreateTilesOfNewMenu = async ({ copiedMenu, dashboardResponse, token }) => {
	const copiedDashboard = await dashboardApi.get(copiedMenu?.dashboard?.key, token)

	if (copiedDashboard?.data?.tiles?.length > 0) {
		const copiedTiles = copiedDashboard?.data?.tiles.map((tile) => ({
			...tile,
			// Update dashboard key of copied menu
			key: undefined,
			dashboardKey: dashboardResponse.data.key,
		}))

		copiedTiles.forEach((dataItem) => {
			tileApi.add({
				dashboardKey: dashboardResponse.data.key,
				data: dataItem,
				token,
			})
		})
	}
}

export const createItemsOfSubmenu = async (copiedMenu, token) => {
	try {
		if (!copiedMenu?.items) return []

		const itemsOfSubmenu = copiedMenu?.items?.map((item) => ({
			...item,
			name: item.name,
			position: item.position,
			dashboard: item.dashboard,
		}))

		const result = []

		for (const dataItem of itemsOfSubmenu) {
			const newDashboard = {
				...dataItem,
				name: dataItem.name,
				position: dataItem.position,
			}

			try {
				const dashboardResponse = await dashboardApi.add(newDashboard, token)

				if (dashboardResponse.data) {
					const newSubMenu = {
						...newDashboard,
						dashboard: { key: dashboardResponse.data.key },
					}

					await processCreateTilesOfNewMenu({
						copiedMenu: dataItem,
						dashboardResponse,
						token,
					})

					result.push(newSubMenu)
				}
			} catch (error) {
				logErrorMessage(error)
			}
		}

		return result
	} catch {
		return []
	}
}
